import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import inscricoesService from "../../../services/inscricoesService";
import type { InscricaoDetalhada } from "../../../services/inscricoesService";
import PlanilhaOrcamentaria from "../../Shared/PlanilhaOrcamentaria";
import type { DadosOrcamento } from "../../../types/orcamento";

const STATUS_COLORS = {
  pendente: "bg-yellow-100 text-yellow-800",
  em_analise: "bg-blue-100 text-blue-800",
  aprovado: "bg-green-100 text-green-800",
  reprovado: "bg-red-100 text-red-800",
};

const STATUS_LABELS = {
  pendente: "Pendente",
  em_analise: "Em Análise",
  aprovado: "Aprovado",
  reprovado: "Reprovado",
};

export default function DetalhesInscricao() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [inscricao, setInscricao] = useState<InscricaoDetalhada | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Ação em andamento
  const [processando, setProcessando] = useState(false);
  const [modalAberto, setModalAberto] = useState<"aprovar" | "reprovar" | null>(
    null
  );
  const [observacoes, setObservacoes] = useState("");

  const carregarInscricao = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await inscricoesService.buscar(parseInt(id));
      setInscricao(response.inscricao);
    } catch (err) {
      setError("Erro ao carregar inscrição");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void carregarInscricao();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleColocarEmAnalise = async () => {
    if (!id || !inscricao) return;

    try {
      setProcessando(true);
      await inscricoesService.colocarEmAnalise(parseInt(id));
      alert("Inscrição colocada em análise!");
      await carregarInscricao();
    } catch (err) {
      alert("Erro ao colocar em análise");
      console.error(err);
    } finally {
      setProcessando(false);
    }
  };

  const handleAprovar = async () => {
    if (!id) return;

    try {
      setProcessando(true);
      await inscricoesService.aprovar(parseInt(id), observacoes || undefined);
      alert("Inscrição aprovada com sucesso!");
      setModalAberto(null);
      setObservacoes("");
      await carregarInscricao();
    } catch (err) {
      alert("Erro ao aprovar inscrição");
      console.error(err);
    } finally {
      setProcessando(false);
    }
  };

  const handleReprovar = async () => {
    if (!id || !observacoes.trim()) {
      alert("As observações são obrigatórias para reprovar");
      return;
    }

    try {
      setProcessando(true);
      await inscricoesService.reprovar(parseInt(id), observacoes);
      alert("Inscrição reprovada");
      setModalAberto(null);
      setObservacoes("");
      await carregarInscricao();
    } catch (err) {
      alert("Erro ao reprovar inscrição");
      console.error(err);
    } finally {
      setProcessando(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Carregando inscrição...</p>
        </div>
      </div>
    );
  }

  if (error || !inscricao) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || "Inscrição não encontrada"}</p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
        >
          ← Voltar
        </button>
      </div>
    );
  }

  const statusFinal =
    inscricao.status === "aprovado" || inscricao.status === "reprovado";

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Botão Voltar */}
      <div className="mb-4">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-600 hover:text-gray-900 transition font-medium"
        >
          ← Voltar
        </button>
      </div>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Inscrição #{inscricao.id}
            </h1>
            <p className="mt-2 text-gray-600">{inscricao.edital.nome}</p>
          </div>
          <span
            className={`px-4 py-2 inline-flex text-sm leading-5 font-semibold rounded-full ${
              STATUS_COLORS[inscricao.status]
            }`}
          >
            {STATUS_LABELS[inscricao.status]}
          </span>
        </div>
      </div>

      {/* Ações */}
      {!statusFinal && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Ações</h2>
          <div className="flex gap-3">
            {inscricao.status === "pendente" && (
              <button
                onClick={handleColocarEmAnalise}
                disabled={processando}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition disabled:opacity-50"
              >
                Colocar em Análise
              </button>
            )}
            <button
              onClick={() => setModalAberto("aprovar")}
              disabled={processando}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition disabled:opacity-50"
            >
              Aprovar
            </button>
            <button
              onClick={() => setModalAberto("reprovar")}
              disabled={processando}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition disabled:opacity-50"
            >
              Reprovar
            </button>
          </div>
        </div>
      )}

      {/* Dados do Proponente */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">
          Dados do Proponente
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600">
              Nome Completo
            </label>
            <p className="mt-1 text-gray-900">
              {inscricao.proponente.nome_completo}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">
              CPF
            </label>
            <p className="mt-1 text-gray-900">{inscricao.proponente.cpf}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">
              Email
            </label>
            <p className="mt-1 text-gray-900">{inscricao.proponente.email}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">
              Telefone
            </label>
            <p className="mt-1 text-gray-900">
              {inscricao.proponente.telefone_celular}
            </p>
          </div>
        </div>
      </div>

      {/* Dados do Formulário */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">
          Proposta Submetida
        </h2>
        <div className="space-y-6">
          {Object.entries(inscricao.schema_campos).map(([key, campo]) => {
            const valor = inscricao.dados_formulario[key];

            const renderValor = () => {
              // Se não tem valor
              if (valor === null || valor === undefined) {
                return (
                  <span className="text-gray-400 italic">Não informado</span>
                );
              }

              // Campo de orçamento
              if (campo.type === "orcamento") {
                const orcamentoData = valor as DadosOrcamento;
                return (
                  <div className="mt-4">
                    <PlanilhaOrcamentaria
                      value={orcamentoData}
                      onChange={() => {}} // readonly
                      config={(campo as any).config}
                      readonly={true}
                    />
                  </div>
                );
              }

              // Campo de arquivo
              if (
                campo.type === "file" &&
                typeof valor === "object" &&
                valor !== null
              ) {
                const fileData = valor as { filename: string; url: string };
                return (
                  <a
                    href={fileData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd" />
                    </svg>
                    {fileData.filename}
                  </a>
                );
              }

              // Array (select múltiplo)
              if (Array.isArray(valor)) {
                return (
                  <span className="text-gray-900">{valor.join(", ")}</span>
                );
              }

              // Texto simples
              return (
                <span className="text-gray-900 whitespace-pre-wrap">
                  {String(valor)}
                </span>
              );
            };

            return (
              <div
                key={key}
                className="border-b border-gray-200 pb-4 last:border-b-0"
              >
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {campo.label}
                  {campo.required && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                <div
                  className={
                    campo.type === "orcamento"
                      ? ""
                      : "bg-gray-50 p-3 rounded border border-gray-200"
                  }
                >
                  {renderValor()}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Análise (se já foi analisada) */}
      {statusFinal && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            Análise Realizada
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600">
                Analisado por
              </label>
              <p className="mt-1 text-gray-900">
                {inscricao.analisado_por?.nome || "-"}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600">
                Data da Análise
              </label>
              <p className="mt-1 text-gray-900">
                {inscricao.analisado_em
                  ? new Date(inscricao.analisado_em).toLocaleString("pt-BR")
                  : "-"}
              </p>
            </div>
          </div>
          {inscricao.observacoes && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-600">
                Observações
              </label>
              <div className="mt-1 text-gray-900 bg-gray-50 p-3 rounded border border-gray-200">
                {inscricao.observacoes}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal Aprovar */}
      {modalAberto === "aprovar" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Aprovar Inscrição
            </h3>
            <p className="text-gray-600 mb-4">
              Tem certeza que deseja aprovar esta inscrição?
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observações (opcional)
              </label>
              <textarea
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                placeholder="Ex: Projeto aprovado com excelência..."
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleAprovar}
                disabled={processando}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition disabled:opacity-50"
              >
                {processando ? "Processando..." : "Confirmar Aprovação"}
              </button>
              <button
                onClick={() => {
                  setModalAberto(null);
                  setObservacoes("");
                }}
                disabled={processando}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Reprovar */}
      {modalAberto === "reprovar" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Reprovar Inscrição
            </h3>
            <p className="text-gray-600 mb-4">
              As observações são <strong>obrigatórias</strong> para reprovar uma
              inscrição.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Justificativa da Reprovação{" "}
                <span className="text-red-500">*</span>
              </label>
              <textarea
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                placeholder="Ex: Orçamento incompatível com a proposta..."
                required
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleReprovar}
                disabled={processando || !observacoes.trim()}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition disabled:opacity-50"
              >
                {processando ? "Processando..." : "Confirmar Reprovação"}
              </button>
              <button
                onClick={() => {
                  setModalAberto(null);
                  setObservacoes("");
                }}
                disabled={processando}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
